import { PianoJWTDatabase, useJWT } from "../state";
import { useCurrentDatabaseId, useCurrentInventoryId } from "./url";

function makePermissions(isAdmin: boolean, isMember: boolean, isVisitor: boolean, db?: PianoJWTDatabase) {
  return {
    canAddGeometry: isAdmin || isMember,
    canAddImage: isAdmin || isMember,
    canAddInventory: isAdmin,
    canAddProject: isAdmin,
    canChangePositionInInvForms: isAdmin || isMember,
    canChangeTitleInInvForms: isAdmin || isMember,
    canCreateFormFromTemplate: isAdmin || isMember,
    canDeleteForm: isAdmin || isMember,
    canDeleteGeom: isAdmin || isMember,
    canDeleteImage: isAdmin || isMember,
    canDeleteProject: isAdmin,
    canListAllInventories: (projectName: string) => {
      if (isAdmin) {
        return true;
      }
      if (db?.project) {
        const project = db.project.find((p) => p.name === projectName);
        return !!project;
      }
      return false;
    },
    canListAllProjects: isAdmin || isMember || isVisitor,
    canMultiedit: isAdmin || isMember,
    canOpenProjectSettings: isAdmin,
    canSaveGeom: isAdmin || isMember,
    canToggleInInvForms: isAdmin || isMember,
    canToggleInProjectSettings: isAdmin,
    getListableInventoryNames: () => db?.project?.flatMap((p) => p.inventory?.map((i) => i.name) || []) || [],
  };
}

const usePermissions = () => {
  const jwt = useJWT();
  const { databaseId: currentDatabase } = useCurrentDatabaseId();
  const { inventoryId: currentInventory } = useCurrentInventoryId();

  if (!currentDatabase || !jwt) {
    return makePermissions(false, false, false);
  } else if (jwt.db.includes(currentDatabase)) {
    // using old format (just the database name in the array)
    return makePermissions(true, true, true, {
      name: currentDatabase,
      role: "admin",
    });
  } else {
    // using new format (object with name property)
    const db = jwt.db.find((db) => typeof db === "object" && db.name === currentDatabase);
    if (db && typeof db === "object") {
      if (db.role === "admin") {
        return makePermissions(true, true, true, db);
      }

      const memberOfSomething = (() => {
        if (db.project) {
          const foundProjectFromJwt = db.project.find((prjObj) => prjObj.name === currentDatabase);
          if (foundProjectFromJwt) {
            if (foundProjectFromJwt.role === "member") {
              return true;
            }

            // Drill down to inventory
            if (!foundProjectFromJwt.inventory) {
              return false;
            }

            if (!currentInventory) {
              return false;
            }
            const foundInvFromJwt = foundProjectFromJwt.inventory.find((invObj) => invObj.name === currentInventory);
            if (foundInvFromJwt && foundInvFromJwt.role === "member") {
              return true;
            }
          }
        }
        return false;
      })();

      return makePermissions(false, memberOfSomething, false, db);
    }
  }
  return makePermissions(false, false, false);
};

export default usePermissions;
